import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './ui/sheet';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, Users, Link, MapPin, Video, User, CheckSquare2, X, Trash2, Edit } from 'lucide-react';
import { PopoverContent, PopoverTrigger } from './ui/popover';
import ResponsiveTooltip from './ui/ResponsiveTooltip';
import DOMPurify from 'dompurify';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  isToday,
  getDay
} from 'date-fns';
import axios from 'axios';
import { baseUrl, meetingAPI, userAPI } from '../Services/apiService';
import { formatRecurrence, truncateText } from '../lib/utils';
import { Popover } from '@radix-ui/react-popover';
import { showToast } from '../utils/toast';
import { handleAPIError } from '../utils/errorHandler';
import CancelRecurringMeetingDialog from './CancelRecurringMeetingDialog';
import EditMeetingModal from './EditMeetingModal';



const CalendarView = ({ showAllMeetings = false, adminView = false }) => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  // Helper function to format status to Title Case
  const formatStatus = (status) => {
    if (!status) return '';
    // Handle specific status mappings
    if (status === 'completed' || status === 'approved') return 'Completed';
    if (status === 'pending' || status === 'in_progress') return 'Pending';
    if (status === 'submitted_for_review') return 'Submitted for Review';
    if (status === 'rejected_in_review') return 'Rejected';
    if (status === 'scheduled' || status === 'upcoming') return 'Upcoming';
    if (status === 'ongoing') return 'Ongoing';

    // Convert to proper title case
    return status
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  // Cancel meeting state
  const [isRecurringCancelDialogOpen, setIsRecurringCancelDialogOpen] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [meetingToCancel, setMeetingToCancel] = useState(null);
  const [isCancelLoading, setIsCancelLoading] = useState(false);

  // Edit meeting state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [meetingToEdit, setMeetingToEdit] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // 640px is sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch users once for the edit modal
  useEffect(() => {
    const fetchUsers = async () => {
      if (!user?.token) return;
      try {
        const response = await userAPI.getUsers();
        setAllUsers(response.data || []);
      } catch (err) {
        console.error('Failed to fetch users for edit modal:', err);
      }
    };
    fetchUsers();
  }, [user?.token]);

  const fetchCalendarMeetings = async () => {
    if (!user?.token) return;
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    try {
      const response = await axios.get(`${baseUrl}/meetings/calendar`, {
        params: {
          start_date: format(monthStart, 'yyyy-MM-dd'),
          end_date: format(monthEnd, 'yyyy-MM-dd')
        },
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (response.status === 200 && response.data) {
        const activeMeetings = response.data.filter(m => m.state !== 'cancelled');
        setMeetings(activeMeetings);
      }
    } catch (error) {
      console.error('Failed to fetch calendar meetings:', error);
    }
  };

  useEffect(() => {
    fetchCalendarMeetings();
  }, [user?.token, currentDate]);

  // Edit meeting handlers
  const handleEditClick = (meeting) => {
    setMeetingToEdit(meeting);
    setIsMeetingModalOpen(false); // close detail modal first
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (meetingId, meetingData, skipNotification = false) => {
    try {
      await meetingAPI.updateMeeting(meetingId, meetingData, skipNotification);
      showToast.success('Meeting updated successfully');
      setIsEditModalOpen(false);
      setMeetingToEdit(null);
      // Refresh calendar data
      await fetchCalendarMeetings();
    } catch (error) {
      handleAPIError(error, 'Failed to update meeting', true);
      throw error; // Let EditMeetingModal handle the error state
    }
  };

  // Cancel meeting handlers
  const handleCancelClick = (meeting) => {
    setMeetingToCancel(meeting);

    // RELIABLE: Check if meeting is part of a recurring series
    // Backend sets recurring_series_id for all instances in a series
    const isRecurring = Boolean(
      meeting.recurring_series_id ||  // PRIMARY: Most reliable indicator
      meeting.is_recurring ||          // Fallback 1: Compatibility
      (meeting.recurrence && meeting.recurrence !== 'none')  // Fallback 2: Legacy check
    );

    if (isRecurring) {
      setIsRecurringCancelDialogOpen(true);
    } else {
      setIsCancelConfirmOpen(true);
    }
  };

  const handleSingleCancelConfirm = async (meeting) => {
    try {
      setIsCancelLoading(true);
      await meetingAPI.cancelMeeting(meeting.id, {
        cancel_all: false,  // Always cancel only this occurrence
        cancel_scope: 'this',
        occurrence_date: meeting.scheduled_date,
      });
      showToast.success('Meeting cancelled successfully');
      await fetchCalendarMeetings();
    } catch (error) {
      console.error('Error cancelling meeting:', error);
      handleAPIError(error, 'Failed to cancel meeting', true);
    } finally {
      setIsCancelLoading(false);
    }
  };

  const handleRecurringCancelConfirm = async (cancel_all, occurrence_date, cancelScope) => {
    if (!meetingToCancel) return;

    try {
      setIsCancelLoading(true);
      await meetingAPI.cancelMeeting(meetingToCancel.id, {
        cancel_all,
        cancel_scope: cancelScope,
        occurrence_date,
      });

      const toastMessage =
        cancelScope === 'this'
          ? 'Meeting occurrence cancelled successfully'
          : cancelScope === 'from_today'
            ? `This meeting and all future occurrences cancelled`
            : 'All future occurrences cancelled (today preserved)';

      showToast.success(toastMessage);
      await fetchCalendarMeetings();
    } catch (error) {
      console.error('Error cancelling recurring meeting:', error);
      handleAPIError(error, 'Failed to cancel meeting', true);
    } finally {
      setIsCancelLoading(false);
      setIsRecurringCancelDialogOpen(false);
      setMeetingToCancel(null);
    }
  };


  let meetingsToShow = meetings;


  // Get meetings for the current month
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get meetings for selected date or all meetings in month
  const getDateMeetings = (date) => {
    if (!date || meetingsToShow.length === 0) return [];

    // Format the target date as YYYY-MM-DD for comparison
    // Use startOfDay to avoid timezone issues
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0); // Normalize to start of day
    const targetDateStr = format(targetDate, 'yyyy-MM-dd');

    // Debug logging for selected date
    if (selectedDate && isSameDay(date, selectedDate)) {
      console.log(`[Calendar] Filtering meetings for selected date: ${targetDateStr}`);
      console.log(`[Calendar] Total meetings available: ${meetingsToShow.length}`);
    }

    const filtered = meetingsToShow.filter(meeting => {
      if (!meeting || !meeting.scheduled_date) return false;

      // Handle date string parsing - backend returns "YYYY-MM-DD" format
      let meetingDateStr;
      if (typeof meeting.scheduled_date === 'string') {
        // Extract just the date part (in case it includes time)
        meetingDateStr = meeting.scheduled_date.split('T')[0];
        // Remove any timezone suffix if present
        meetingDateStr = meetingDateStr.split('+')[0].split('Z')[0];
      } else if (meeting.scheduled_date instanceof Date) {
        // Normalize to start of day to avoid timezone issues
        const meetingDate = new Date(meeting.scheduled_date);
        meetingDate.setHours(0, 0, 0, 0);
        meetingDateStr = format(meetingDate, 'yyyy-MM-dd');
      } else {
        // Try to parse as date object
        try {
          const parsedDate = new Date(meeting.scheduled_date);
          parsedDate.setHours(0, 0, 0, 0); // Normalize to start of day
          meetingDateStr = format(parsedDate, 'yyyy-MM-dd');
        } catch (e) {
          console.warn('[Calendar] Could not parse meeting date:', meeting.scheduled_date, meeting);
          return false;
        }
      }

      // Direct string comparison is more reliable
      const matches = meetingDateStr === targetDateStr;

      // Debug logging for first few matches
      if (selectedDate && isSameDay(date, selectedDate) && matches) {
        console.log(`[Calendar] Match found: Meeting "${meeting.title}" on ${meetingDateStr} matches ${targetDateStr}`);
      }

      return matches;
    });

    // Debug logging for selected date
    if (selectedDate && isSameDay(date, selectedDate)) {
      console.log(`[Calendar] Found ${filtered.length} meeting(s) for ${targetDateStr}:`,
        filtered.map(m => ({ title: m.title, scheduled_date: m.scheduled_date }))
      );
    }

    return filtered;
  };

  const selectedDateMeetings = selectedDate
    ? getDateMeetings(selectedDate).sort((a, b) => {
      // Smart ordering: ongoing → upcoming → completed → cancelled
      const statusOrder = {
        'ongoing': 1,
        'upcoming': 2,
        'completed': 3,
        'cancelled': 4
      };

      const aOrder = statusOrder[a.state] || 99;
      const bOrder = statusOrder[b.state] || 99;

      // First sort by status
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      // Within same status, sort by start_time (earliest first)
      if (!a.start_time && !b.start_time) return 0;
      if (!a.start_time) return 1; // No time goes to end
      if (!b.start_time) return -1;
      return a.start_time.localeCompare(b.start_time);
    })
    : [];

  // Navigation functions
  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleMeetingClick = (meeting, event) => {
    event.stopPropagation(); // Prevent date selection when clicking meeting
    setSelectedMeeting(meeting);
    setIsMeetingModalOpen(true);
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getMeetingStatusColor = (meeting) => {
    switch (meeting.state) {
      case 'completed': return 'bg-[#A7F3D0] text-[#065F46] border-green-500';
      case 'ongoing': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'upcoming': return 'bg-[#BFDBFE] text-[#1E3A8A] border-blue-200';
      default: return 'bg-red-100 text-gray-800 border-gray-200';
    }
  };

  // Create calendar grid with proper week layout
  const createCalendarGrid = () => {
    const startDate = new Date(monthStart);
    const startDay = getDay(startDate); // 0 = Sunday, 1 = Monday, etc.

    // Add empty cells for days before the month starts
    const calendarDays = [];
    for (let i = 0; i < startDay; i++) {
      calendarDays.push(null);
    }

    // Add all days of the month
    monthDays.forEach(day => {
      calendarDays.push(day);
    });

    return calendarDays;
  };

  const calendarGrid = createCalendarGrid();

  return (
    <div className="space-y-6">
      <Card className="glass-card border-0">
        {/* <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-yellow-600" />
            {showAllMeetings || adminView ? 'Organization Calendar' : 'My Calendar'}
          </CardTitle>
          <CardDescription>
            {showAllMeetings || adminView ? 'All meetings across the organization' : 'Your meetings and schedule'}
          </CardDescription>
        </CardHeader> */}
        <CardContent className="space-y-2">
          {/* Calendar Header */}
          <div className="flex items-center justify-between">
            <div className="sm:flex flex-col items-center gap-4 mt-2">
              <div className="flex gap-2 items-center">
                <h2 className="text-sm sm:text-xl font-semibold text-gray-900" data-testid="calendar-month-year">
                  {format(currentDate, 'MMMM yyyy')}
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToToday}
                  className="text-yellow-600 border-yellow-300 hover:bg-yellow-50 sm:w-12 sm:h-10 w-10 h-8 p-1 "
                  data-testid="go-to-today-btn"
                >
                  Today
                </Button>
              </div>
              <div>
                <div className="sm:hidden flex flex-wrap mt-1  gap-4 items-center">
                  {/* calendar color guide */}

                  <div className='flex  items-center gap-1.5'>
                    <div className='w-2 h-2 bg-yellow-100 rounded-full'></div>
                    <span className="text-xs ">Ongoing</span>
                  </div>

                  <div className='flex  items-center gap-1.5'>
                    <div className='w-2 h-2 bg-amber-100 rounded-full'></div>
                    <span className="text-xs ">Pending</span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-[#BFDBFE] rounded-full"></div>
                    <span className="text-xs text-[#1E3A8A]">Upcoming</span>
                  </div>
                  <div className="flex  items-center gap-1.5">
                    <div className="w-2 h-2 bg-[#A7F3D0] rounded-full"></div>
                    <span className="text-xs text-[#065F46]">Completed</span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-red-100 rounded-full"></div>
                    <span className="text-xs text-red-800">Cancelled</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="sm:flex hidden  gap-4 items-center">
              {/* calendar color guide */}

              <div className='flex items-center gap-1.5'>
                <div className='w-2 h-2 bg-yellow-100 rounded-full'></div>
                <span className="text-xs ">Ongoing</span>
              </div>

              <div className='flex items-center gap-1.5'>
                <div className='w-2 h-2 bg-amber-100 rounded-full'></div>
                <span className="text-xs ">Pending</span>
              </div>

              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-[#BFDBFE] rounded-full"></div>
                <span className="text-xs text-[#1E3A8A]">Upcoming</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-[#A7F3D0] rounded-full"></div>
                <span className="text-xs text-[#065F46]">Completed</span>
              </div>

              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-red-100 rounded-full"></div>
                <span className="text-xs text-red-800">Cancelled</span>
              </div>
            </div>
            <div className="flex  gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousMonth}
                data-testid="previous-month-btn"
                className="sm:w-8 sm:h-8 w-6 h-6 "
              >
                <ChevronLeft className="w-2 h-2" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextMonth}
                data-testid="next-month-btn"
                className="sm:w-8 sm:h-8 w-6 h-6 "
              >
                <ChevronRight className="w-2 h-2" />
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden ">
            {/* Week Days Header */}
            <div className="grid grid-cols-7 sm:grid-cols-7 border-b border-gray-200">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-1 text-center text-sm  font-medium text-gray-500 bg-gray-50">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days Grid */}
            <div className="grid grid-cols-7">
              {calendarGrid.map((day, index) => {
                if (!day) {
                  return <div key={index} className="h-28  sm:h-24 border-b border-r border-gray-100"></div>;
                }

                const dayMeetings = getDateMeetings(day);

                // Debug: Log if a specific day has meetings (only for first few days to avoid spam)
                if (index < 5 && dayMeetings.length > 0) {
                  console.log(`Calendar Day ${format(day, 'yyyy-MM-dd')}: Found ${dayMeetings.length} meeting(s)`,
                    dayMeetings.map(m => ({ title: m.title, scheduled_date: m.scheduled_date }))
                  );
                }

                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const isTodayDate = isToday(day);
                const isCurrentMonth = isSameMonth(day, currentDate);

                return (
                  <div
                    key={day.toString()}
                    className={`h-28 sm:h-24   border-b border-r border-gray-100 p-1 cursor-pointer transition-colors ${isSelected ? 'bg-yellow-50 border-yellow-300' :
                      isTodayDate ? 'bg-blue-50' :
                        'hover:bg-gray-50'
                      } ${!isCurrentMonth ? 'text-gray-300' : ''}`}
                    onClick={() => setSelectedDate(day)}
                    data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <div className={`text-sm font-medium  mb-2 ${isTodayDate ? 'text-blue-600' :
                      isSelected ? 'text-yellow-600' :
                        !isCurrentMonth ? 'text-gray-300' : 'text-gray-900'
                      }`}>
                      {format(day, 'd')}
                    </div>
                    <div className="space-y-0.5">
                      {dayMeetings.length === 0 ? (
                        // Debug: Show if no meetings found (remove in production)
                        null
                      ) : (
                        <>
                          {dayMeetings.slice(0, 2).map((meeting, idx) => {
                            // Use a unique key combining meeting id and date to handle recurring meetings
                            const uniqueKey = `${meeting.id}-${format(day, 'yyyy-MM-dd')}-${idx}`;
                            const isRecurring = meeting.recurrence && meeting.recurrence !== 'none';
                            const statusColor = getMeetingStatusColor(meeting);
                            return (
                              <div
                                key={uniqueKey}

                                className={`text-xs px-1  h-5 rounded truncate cursor-pointer hover:opacity-80 transition-colors border ${statusColor} ${isRecurring ? 'border-2' : ''
                                  }`}
                                title={`${meeting.title}${isRecurring ? ` (${meeting.recurrence_label || formatRecurrence(meeting.recurrence)})` : ''} - ${meeting.scheduled_date}`}
                                onClick={(e) => handleMeetingClick(meeting, e)}
                                data-testid={`calendar-meeting-${meeting.id}-${format(day, 'yyyy-MM-dd')}`}
                              >
                                {formatTime(meeting.start_time)} {meeting.title}
                              </div>
                            );
                          })}
                          {dayMeetings.length > 2 && (
                            <div className="text-xs  text-gray-500 px-2 font-medium">
                              +{dayMeetings.length - 2}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Date Details - Side Panel */}
      <Sheet open={!!selectedDate} onOpenChange={(open) => !open && setSelectedDate(null)}>
        <SheetContent side="right" className="w-4/8 sm:w-1/2 max-w-none flex flex-col p-0">
          <div className="sticky top-9 z-10 bg-white border-b px-6 pt-6 pb-4">
            <SheetHeader>
              <SheetTitle className="flex items-center justify-between">
                <span className='text-wrap'>Meetings on {selectedDate && format(selectedDate, 'EEEE, MMMM do, yyyy')}  <span className='border border-b-2 border-yellow-200 text-green-600 text-nowrap p-1 rounded-md'>{selectedDateMeetings.length} meeting(s)</span></span>

              </SheetTitle>


            </SheetHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <div className="mt-6">
              {selectedDateMeetings.length === 0 ? (
                <div className="text-center py-12" data-testid="no-meetings-selected-date">
                  <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No meetings upcoming for this date</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedDateMeetings.map(meeting => (
                    <Card
                      key={meeting.id}
                      className="hover:shadow-md transition-shadow"
                      data-testid={`selected-date-meeting-${meeting.id}`}
                    >
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg pr-4 text-wrap">{meeting.title}</CardTitle>
                        <div className="flex items-start ">

                          <Badge className={`${getMeetingStatusColor(meeting)} hover:!bg-green-100  shrink-0`}>
                            {formatStatus(meeting.state)}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {/* Time */}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="font-medium">{formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}</span>
                        </div>



                        {/* Organizer */}
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <User className="w-4 h-4 text-gray-500" />
                          <span>
                            <span className="text-gray-500">Organizer:</span>{' '}
                            <span className="font-medium">{meeting.organizer?.full_name || meeting.organizer?.name || 'Unknown'}</span>
                          </span>
                        </div>

                        {/* Participants */}
                        <div className="flex items-start gap-2 text-sm text-gray-600">
                          <Users className="w-4 h-4 text-gray-500 mt-0.5" />
                          <div className="flex-1">
                            <span className="text-gray-500">Participants ({meeting.participant_count || 0}):</span>
                            <div className="mt-1 space-y-0.5">
                              {meeting.participants && meeting.participants.length > 0 ? (
                                meeting.participants.slice(0, 3).map((participant, index) => (
                                  <div key={participant.id || index} className="flex items-center gap-1.5 text-xs">
                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                    <span className="text-gray-700">{participant.full_name || participant.email}</span>
                                  </div>
                                ))
                              ) : (
                                <span className="text-xs text-gray-500">No participants</span>
                              )}
                              {meeting.participants && meeting.participants.length > 3 && (
                                <ResponsiveTooltip
                                  content="Please click on full details to see full participation"
                                  side="top"

                                >
                                  <div className="text-xs text-blue-600 font-medium cursor-pointer hover:underline">
                                    +{meeting.participants.length - 3} more
                                  </div>
                                </ResponsiveTooltip>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Meeting Link */}
                        {meeting.join_url && (
                          <div className="flex items-start gap-2 text-sm pt-2 border-t">
                            <Link className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-500 mb-1">Meeting Link:</p>
                              <p className="text-gray-700 break-words mb-2">{meeting.join_url}</p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(meeting.join_url, '_blank')}
                                className="text-xs"
                              >
                                <Link className="w-3 h-3 mr-1" />
                                Join Meeting
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Agenda */}
                        {meeting.agenda && (
                          <div className="pt-3 border-t">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Agenda:</h4>
                            {isMobile ? (
                              /* Mobile: Popover on click to show full agenda */
                              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    {meeting.agenda.length > 20 ? (
                                      <span
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(truncateText(meeting.agenda, 80)) }}
                                        className="text-sm text-gray-600 hover:underline cursor-pointer inline-block"
                                      />
                                    ) : (
                                      <span
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(meeting.agenda) }}
                                        className="text-sm text-gray-600"
                                      />
                                    )}
                                  </PopoverTrigger>
                                  <PopoverContent className="w-72 p-3" align="start">
                                    <div
                                      className="text-sm text-gray-600 text-wrap overflow-x-hidden"
                                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(meeting.agenda) }}
                                    />
                                  </PopoverContent>
                                </Popover>
                              </p>
                            ) : (
                              /* Desktop: hover shows "Click to view full details" overlay */
                              <div
                                className="relative group cursor-pointer"
                                onClick={(e) => handleMeetingClick(meeting, e)}
                              >
                                <div
                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(truncateText(meeting.agenda, 80)) }}
                                  className="text-sm text-gray-600 whitespace-pre-wrap"
                                />
                                <div className="absolute inset-0 bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded">
                                  <span className="text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-1 rounded-full shadow-sm">
                                    Click to view full details
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* View Details Button */}
                        <div className="pt-2 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleMeetingClick(meeting, e)}
                            className="w-full"
                          >
                            View Full Details
                          </Button>
                        </div>

                        {/* Edit Meeting Button — visible to organizer/admin for upcoming/ongoing */}
                        {(meeting.state === 'upcoming' || meeting.state === 'ongoing' || meeting.state === 'pending') &&
                          (user?.role === 'admin' || meeting.organizer?.id === user?.id) && (
                            <div className="pt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditClick(meeting)}
                                className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Meeting
                              </Button>
                            </div>
                          )}

                        {/* Cancel Meeting Button */}
                        {(
                          (meeting.recurring_series_id || meeting.is_recurring || (meeting.recurrence && meeting.recurrence !== 'none'))
                            ? (meeting.state !== 'cancelled' && (meeting.has_future_cancellable_instances !== false || meeting.state === 'upcoming'))
                            : meeting.state === 'upcoming'
                        ) && (
                            user?.role === 'admin' || meeting.organizer?.id === user?.id
                          ) && (
                            <div className="pt-2">
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleCancelClick(meeting)}
                                disabled={isCancelLoading}
                                className="w-full"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {(meeting.state === 'completed' || meeting.state === 'ongoing' || meeting.state === 'pending')
                                  ? 'Cancel Future Occurrences'
                                  : 'Cancel Meeting'}
                              </Button>
                            </div>
                          )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Meeting Details Modal large screen*/}
      {!isMobile && (
        <Dialog open={isMeetingModalOpen} onOpenChange={setIsMeetingModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-yellow-600" />
                Meeting Details
              </DialogTitle>
              <DialogDescription>
                Complete information about the selected meeting
              </DialogDescription>
            </DialogHeader>

            {selectedMeeting && (
              <div className="space-y-6">
                {/* Meeting Header */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {selectedMeeting.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{formatTime(selectedMeeting.start_time)} - {formatTime(selectedMeeting.end_time)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        <span>{format(new Date(selectedMeeting.scheduled_date), 'EEEE, MMMM do, yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full border border-blue-100">
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="font-medium">{selectedMeeting.location || 'No location set'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full border border-purple-100">
                        <Video className="w-3.5 h-3.5" />
                        <span className="font-medium capitalize">{selectedMeeting.meeting_type || 'Unspecified'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Meeting Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-1 gap-1">
                  {/* Organizer */}
                  <div className='flex gap-10'>
                    <div className="space-y-2 ">
                      <h4 className="font-medium text-gray-900 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Organizer
                      </h4>
                      <div className='flex gap-5 items-center flex-wrap'>
                        <p className="text-gray-600">{selectedMeeting.organizer?.full_name || selectedMeeting.organizer?.name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{selectedMeeting.organizer?.email || ''}</p>
                      </div>
                    </div>

                    {/* Participants */}
                    <div className="max-h-40  overflow-y-auto space-y-2">
                      <h4 className="font-medium sticky top-0 bg-white text-gray-900 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Participants ({selectedMeeting.participant_count || 0})
                      </h4>
                      <div className="space-y-1">
                        {selectedMeeting.participants && selectedMeeting.participants.length > 0 ? (
                          selectedMeeting.participants.map((participant, index) => (
                            <div key={participant.id || index} className="flex items-center gap-2 text-sm">
                              {/* <div className="w-2 h-2 bg-blue-500 rounded-full"></div> */}
                              <span className="text-gray-700 text-nowrap">{participant.full_name || participant.email}</span>
                              <span className="text-gray-400 text-xs text-wrap">({participant.email})</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-sm">No participants</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Meeting Link (if available) */}
                  <div className='flex justify-between'>
                    <div className="space-y-2 mt-2">
                      <h4 className="font-medium text-gray-900 flex items-center gap-2">
                        <Link className="w-4 h-4" />
                        Meeting Link
                      </h4>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">
                          {selectedMeeting.join_url || 'No meeting link provided'}
                        </p>
                        {selectedMeeting.join_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => window.open(selectedMeeting.join_url, '_blank')}
                          >
                            Join Meeting
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Meeting Status */}
                    <div className="space-y-2 mt-2">
                      <h4 className="font-medium text-gray-900">Status</h4>
                      <Badge className={`${getMeetingStatusColor(selectedMeeting)}`}>
                        {formatStatus(selectedMeeting.state)}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Meeting Agenda (if available) */}
                {selectedMeeting.agenda && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">Agenda</h4>
                    <p className="text-gray-600 overflow-y-auto text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: selectedMeeting.agenda }} />
                  </div>
                )}

                {/* Edit Meeting Button — desktop modal */}
                {(selectedMeeting.state === 'upcoming' || selectedMeeting.state === 'ongoing' || selectedMeeting.state === 'pending') &&
                  (user?.role === 'admin' || selectedMeeting.organizer?.id === user?.id) && (
                    <div className="pt-3 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClick(selectedMeeting)}
                        className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Meeting
                      </Button>
                    </div>
                  )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Meeting Details Modal mobile screen*/}
      {isMobile && (
        <Dialog open={isMeetingModalOpen} onOpenChange={setIsMeetingModalOpen}>
          <DialogContent className="max-w-[95vw] mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pb-2  top-0 ">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <CalendarIcon className="w-5 h-5 text-yellow-600 shrink-0" />
                <span className="break-words">Meeting Details</span>
              </DialogTitle>
              <DialogDescription className="text-xs">
                Complete information about the selected meeting
              </DialogDescription>
            </DialogHeader>

            {selectedMeeting && (
              <div className="space-y-5 -mt-2">
                {/* Meeting Header */}
                <div className="space-y-3 pb-3 border-b">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 break-words">
                      {selectedMeeting.title}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" />
                        <span className="break-words">
                          {formatTime(selectedMeeting.start_time)} - {formatTime(selectedMeeting.end_time)}
                        </span>
                      </div>
                      <div className="flex items-start gap-2 text-sm text-gray-600">
                        <CalendarIcon className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" />
                        <span className="break-words">
                          {format(new Date(selectedMeeting.scheduled_date), 'EEEE, MMMM do, yyyy')}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 flex-wrap mt-3">
                        <div className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
                          <MapPin className="w-3.5 h-3.5 shrink-0" />
                          <span className="font-semibold">{selectedMeeting.location || 'No location set'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full border border-purple-100">
                          <Video className="w-3.5 h-3.5 shrink-0" />
                          <span className="font-semibold capitalize">{selectedMeeting.meeting_type || 'Unspecified'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Meeting Information - Stacked Vertically */}
                <div className="space-y-4">
                  {/* Meeting Status */}
                  <div className="space-y-2 pb-3 border-b">
                    <h4 className="font-medium text-gray-900 text-sm">Status</h4>
                    <Badge className={`${getMeetingStatusColor(selectedMeeting)} text-xs`}>
                      {formatStatus(selectedMeeting.state)}
                    </Badge>
                  </div>

                  {/* Organizer */}
                  <div className="flex gap-10  pb-3 border-b">
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Organizer
                      </h4>
                      <p className="text-gray-600 text-sm break-words">
                        {selectedMeeting.organizer?.full_name || selectedMeeting.organizer?.name || 'Unknown'}
                      </p>
                      {selectedMeeting.organizer?.email && (
                        <p className="text-xs text-gray-500 break-all">{selectedMeeting.organizer.email}</p>
                      )}
                    </div>
                    <div className="space-y-2 pb-3 ">
                      <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Participants ({selectedMeeting.participant_count || 0})
                      </h4>
                      <div className="space-y-1.5">
                        {selectedMeeting.participants && selectedMeeting.participants.length > 0 ? (
                          selectedMeeting.participants.map((participant, index) => (
                            <div key={participant.id || index} className="flex items-start gap-1.5 text-xs">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                              <div className="flex flex-col min-w-0">
                                <span className="text-gray-700 font-medium break-words">{participant.full_name || participant.email}</span>
                                <span className="text-gray-400 text-[10px] break-all">{participant.email}</span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-xs">No participants</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Participants Section Removed - Now integrated above */}


                  {/* Meeting Link (if available) */}
                  <div className="space-y-2 pb-3 border-b">
                    <h4 className="font-medium text-gray-900 text-sm flex items-center gap-2">
                      <Link className="w-4 h-4" />
                      Meeting Link
                    </h4>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      {selectedMeeting.join_url ? (
                        <>
                          <p className="text-xs text-gray-600 break-all mb-3">
                            {selectedMeeting.join_url}
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full"
                            onClick={() => window.open(selectedMeeting.join_url, '_blank')}
                          >
                            <Link className="w-4 h-4 mr-2" />
                            Join Meeting
                          </Button>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500">No meeting link provided</p>
                      )}
                    </div>
                  </div>

                  {/* Meeting Agenda (if available) */}
                  {selectedMeeting.agenda && (
                    <div className="space-y-2 pb-2">
                      <h4 className="font-medium text-gray-900 text-sm">Agenda</h4>
                      <p
                        className="text-gray-600 text-sm leading-relaxed break-words"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedMeeting.agenda) }}
                      />
                    </div>
                  )}

                  {/* Edit Meeting Button — mobile modal */}
                  {(selectedMeeting.state === 'upcoming' || selectedMeeting.state === 'ongoing' || selectedMeeting.state === 'pending') &&
                    (user?.role === 'admin' || selectedMeeting.organizer?.id === user?.id) && (
                      <div className="pt-3 border-t mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditClick(selectedMeeting)}
                          className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Meeting
                        </Button>
                      </div>
                    )}

                  {/* Cancel Meeting Button */}
                  {selectedMeeting && (
                    (selectedMeeting.recurring_series_id || selectedMeeting.is_recurring || (selectedMeeting.recurrence && selectedMeeting.recurrence !== 'none'))
                      ? (selectedMeeting.state !== 'cancelled' && (selectedMeeting.has_future_cancellable_instances !== false || selectedMeeting.state === 'upcoming'))
                      : selectedMeeting.state === 'upcoming'
                  ) && (
                      user?.role === 'admin' || selectedMeeting.organizer?.id === user?.id
                    ) && (
                      <div className="pt-3 border-t mt-3">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleCancelClick(selectedMeeting)}
                          disabled={isCancelLoading}
                          className="w-full"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {(selectedMeeting.state === 'completed' || selectedMeeting.state === 'ongoing' || selectedMeeting.state === 'pending')
                            ? 'Cancel Future Occurrences'
                            : 'Cancel Meeting'}
                        </Button>
                      </div>
                    )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Confirmation Dialog for Single Meeting Cancellation */}
      <AlertDialog open={isCancelConfirmOpen} onOpenChange={setIsCancelConfirmOpen}>
        <AlertDialogContent className="sm:max-w-lg w-[90vw] rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel <strong>{meetingToCancel?.title}</strong>?
              <br />
              <span className="text-sm text-gray-600">
                {meetingToCancel?.scheduled_date && format(new Date(meetingToCancel.scheduled_date), 'EEEE, MMMM dd, yyyy')}
                {meetingToCancel?.start_time && ` at ${meetingToCancel.start_time}`}
              </span>
              <br />
              <br />
              {(meetingToCancel?.recurring_series_id || meetingToCancel?.is_recurring) ? (
                <em className="text-amber-600">This will cancel only this occurrence, not the entire series.</em>
              ) : (
                <span className="text-red-600">This action cannot be undone.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelLoading}>No, Keep Meeting</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (meetingToCancel) {
                  handleSingleCancelConfirm(meetingToCancel);
                  setIsCancelConfirmOpen(false);
                }
              }}
              disabled={isCancelLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {isCancelLoading ? 'Cancelling...' : 'Yes, Cancel Meeting'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Recurring Meeting Dialog */}
      <CancelRecurringMeetingDialog
        open={isRecurringCancelDialogOpen}
        onClose={() => {
          setIsRecurringCancelDialogOpen(false);
          setMeetingToCancel(null);
        }}
        meeting={meetingToCancel}
        onConfirm={handleRecurringCancelConfirm}
        isLoading={isCancelLoading}
      />

      {/* Edit Meeting Modal */}
      <EditMeetingModal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setMeetingToEdit(null);
        }}
        meeting={meetingToEdit}
        onSubmit={handleEditSubmit}
        users={allUsers}
        allowFollowingScope={true}
      />
    </div>
  );
};

export default CalendarView;